// Designed WYSIWYG LDP editor — renders the audio-course landing page in the LIVE jade+gold storefront
// design (the same landing.css Blitzscale renders), with every line editable in place:
//   - {{slots}} (headline, pain scene, dropdowns, bonuses, …) edit the per-product FIELDS.
//   - any fixed-prose line (intros, origin story, analogies) edits a per-product PROSE override,
//     keyed by its 0-based line index in the template body (matches the backend renderer).
// Store-injected vars ({{your_price}} etc.) render as read-only chips; derived vars (title, destination)
// render resolved. Edits flow up via onField / onProse; the panel autosaves them.
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { listOutputs, makeSample, makeSamplePage, type LdpMeta } from '../lib/audio-course-client';

// book-master language for a store code (mirrors the audio-course bundler: NPI_SAR → NPI, else unchanged).
const bookLangOf = (lang: string): string => (({ NPI_SAR: 'NPI' } as Record<string, string>)[lang] || lang);

type Fields = Record<string, string>;
type Prose = Record<string, string>;

// The live LDP design system (jade + gold, dark), scoped under .ldpv — inlined from the storefront's
// landing.css so the editor matches what Blitzscale renders. Mockup-only chrome is dropped.
const LANDING_CSS = `
.ldpv{--ground:#0E1316;--surface:#161E23;--raised:#1F2A31;--hair:#28353D;--hair-soft:#202C33;--text:#ECF2F3;--muted:#95A7AE;--faint:#5F717A;--jade:#47C9AB;--jade-dim:#2B7C6B;--gold:#F0A63C;--savings:#63D68E;--sans:"Noto Sans",ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;--serif:"Noto Serif","Iowan Old Style",Palatino,Georgia,serif;--mono:"Noto Sans Mono",ui-monospace,Menlo,Consolas,monospace;--measure:70ch;background:var(--ground);color:var(--text);font-family:var(--sans);font-size:16px;line-height:1.6;-webkit-font-smoothing:antialiased;border-radius:12px;overflow:hidden}
.ldpv *{box-sizing:border-box}
.ldpv .ldp-page{max-width:720px;margin:0 auto;padding:0 22px 3rem}
.ldpv strong{color:var(--text);font-weight:700}
.ldpv mark{font-weight:800;color:var(--jade);background:rgba(71,201,171,.13);padding:.02em .32em;border-radius:5px}
.ldpv .py{font-family:var(--serif);color:var(--jade)}
.ldpv .hazard{height:3px;background:linear-gradient(90deg,var(--jade),var(--gold))}
.ldpv .hero{padding:44px 0 32px;border-bottom:1px solid var(--hair);text-align:center}
.ldpv .eyebrow{font-family:var(--mono);font-size:clamp(18px,4vw,34px);line-height:1.15;letter-spacing:.01em;text-transform:uppercase;font-weight:600;color:var(--gold);margin:0 0 16px;text-wrap:balance}
.ldpv .hero h1{font-size:clamp(34px,7vw,68px);line-height:.96;letter-spacing:-.03em;font-weight:800;margin:0 0 14px;text-wrap:balance}
.ldpv .subhead{font-family:var(--serif);font-size:20px;line-height:1.5;color:var(--text);max-width:54ch;margin:0 auto}
.ldpv .phrases{list-style:none;padding:0;margin:22px auto 0;max-width:34rem;text-align:left;display:grid;gap:10px}
.ldpv .phrases li{position:relative;padding-left:30px;font-family:var(--serif);font-size:17px;line-height:1.5;color:#D9E2E4}
.ldpv .phrases li::before{content:"\\2713";position:absolute;left:0;top:0;color:var(--jade);font-weight:800}
.ldpv .cta{display:inline-block;font:800 17px/1 var(--sans);color:#1a1205;background:linear-gradient(180deg,#F6B54E,var(--gold));border:0;border-radius:11px;padding:15px 30px;box-shadow:0 10px 26px -10px rgba(240,166,60,.6)}
.ldpv .cta-wrap{text-align:center;margin:22px 0 0}
.ldpv section{padding:40px 0;border-top:1px solid var(--hair-soft)}
.ldpv section>*:first-child{margin-top:0}
.ldpv .kicker{display:block;font-family:var(--mono);font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:var(--jade);margin:0 0 14px}
.ldpv h2{font-size:clamp(24px,4.4vw,36px);line-height:1.08;letter-spacing:-.02em;font-weight:800;margin:0 0 18px;text-wrap:balance}
.ldpv p{font-family:var(--serif);font-size:18px;line-height:1.6;color:#D9E2E4;margin:0 0 16px;max-width:var(--measure)}
.ldpv .lead{font-size:20px;color:var(--text)}
.ldpv .marks{list-style:none;padding:0;margin:12px 0 0;display:grid;gap:10px;max-width:var(--measure)}
.ldpv .marks li{position:relative;padding-left:28px;font-family:var(--serif);font-size:17px;line-height:1.55;color:#D9E2E4}
.ldpv .marks li::before{content:"\\203A";position:absolute;left:0;top:0;color:var(--jade);font-weight:800;font-size:1.2em}
.ldpv .gloss{font:600 13.5px/1.7 var(--mono);color:var(--jade);background:var(--ground);border:1px solid var(--hair-soft);border-radius:10px;padding:12px 14px;margin:14px 0 0;white-space:pre-wrap}
.ldpv .acc{background:var(--surface);border:1px solid var(--hair);border-radius:12px;margin-top:12px;overflow:hidden}
.ldpv .acc-h{display:flex;align-items:center;gap:12px;padding:16px 18px;font:800 clamp(16px,2.4vw,20px)/1.25 var(--sans);color:var(--text)}
.ldpv .acc-h .ic{margin-left:auto;flex:none;width:24px;height:24px;display:grid;place-items:center;border:1px solid var(--jade-dim);border-radius:50%;color:var(--jade);font-family:var(--mono);font-weight:700}
.ldpv .acc-b{padding:2px 18px 18px;border-top:1px solid var(--hair-soft)}
.ldpv .reveal{text-align:center}
.ldpv .reveal .big{font-size:clamp(52px,12vw,96px);font-weight:800;letter-spacing:-.03em;font-family:var(--mono);line-height:1;margin:12px 0 0;background:linear-gradient(180deg,#fff,#cfe9e2);-webkit-background-clip:text;background-clip:text;color:transparent}
.ldpv .scarcity{background:linear-gradient(180deg,rgba(240,166,60,.09),transparent);border:1px solid rgba(240,166,60,.25);border-radius:12px;text-align:center;padding:24px;margin:.5rem 0;font-weight:700}
.ldpv .bonus{background:var(--surface);border:1px solid var(--hair);border-radius:12px;padding:16px 20px;margin-top:12px}
.ldpv .bonus .tag{font-family:var(--mono);font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--gold)}
.ldpv .fund{margin-top:1.2rem;padding:18px 20px;background:#0B1013;border:1px solid var(--hair);border-radius:12px;font-family:var(--serif);color:#CBD6D9}
.ldpv .ps{font-family:var(--serif);font-size:17px;line-height:1.6;color:var(--muted)}
.ldpv .chip{font-family:var(--mono);font-size:12px;background:var(--raised);border:1px solid var(--hair);color:var(--muted);border-radius:6px;padding:1px 7px;white-space:nowrap}
/* editing affordances */
.ldpv .ed{display:block;width:100%;background:transparent;color:inherit;font:inherit;border:1px dashed transparent;border-radius:6px;padding:2px 6px;margin:-2px -6px;resize:none;overflow:hidden}
.ldpv .ed:hover{border-color:var(--hair)}
.ldpv .ed:focus{outline:none;border-color:var(--jade);background:rgba(71,201,171,.06)}
.ldpv .ed-in{display:inline;width:auto;min-width:4ch;background:rgba(71,201,171,.10);color:var(--jade);font:inherit;border:0;border-bottom:1px solid var(--jade-dim);padding:0 3px}
.ldpv .ed-in:focus{outline:none;border-bottom-color:var(--jade)}
.ldpv .ed-empty{border-color:rgba(240,166,60,.5)!important}
.ldpv .slotlabel{font-family:var(--mono);font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--faint);display:block;margin:8px 0 2px}
`;

// ---- inline markup (**bold**, ==hl==, **==brand==**) → HTML, for read-only fixed spans ----
function mk(s: string): string {
  const esc = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc
    .replace(/\*\*==(.+?)==\*\*/g, '<strong><mark>$1</mark></strong>')
    .replace(/==(.+?)==/g, '<mark>$1</mark>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

// auto-grow a textarea to its content
function grow(el: HTMLTextAreaElement | null) { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }

// ---- one editable block field (multi-line) ----
function BlockEd(p: { value: string; onChange: (v: string) => void; label?: string; placeholder?: string; empty?: boolean; style?: CSSProperties }) {
  return (
    <>
      {p.label && <span className="slotlabel">{p.label}</span>}
      <textarea
        className={`ed${p.empty ? ' ed-empty' : ''}`} value={p.value} placeholder={p.placeholder}
        style={p.style} ref={grow}
        onChange={(e) => { grow(e.target); p.onChange(e.target.value); }}
      />
    </>
  );
}

// ---- one editable inline slot (short, in the flow of a sentence) ----
function InlineEd(p: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const w = Math.max(4, Math.min(40, (p.value || p.placeholder).length + 1));
  return <input className="ed-in" size={w} value={p.value} placeholder={p.placeholder} onChange={(e) => p.onChange(e.target.value)} />;
}

// ---- template parsing: lines with source indices, grouped by "**N · …**" block markers ----
type Line = { i: number; text: string };
type Block = { n: number | null; section: string | null; lines: Line[] };

function parseBlocks(body: string): Block[] {
  const out: Block[] = [];
  let cur: Block = { n: null, section: null, lines: [] };
  body.split('\n').forEach((raw, i) => {
    const text = raw.replace(/\s+$/, '');
    const bm = /^\*\*(\d+) · .+\*\*$/.exec(text);
    const sm = /^## (.+)$/.exec(text);
    if (bm) { out.push(cur); cur = { n: Number(bm[1] ?? ''), section: null, lines: [] }; return; }
    if (sm) { out.push(cur); cur = { n: null, section: sm[1] ?? '', lines: [] }; return; }
    cur.lines.push({ i, text });
  });
  out.push(cur);
  return out.filter((b) => b.n !== null || b.section !== null || b.lines.some((l) => l.text.trim()));
}

const slotRe = /\{\{(\w+)\}\}/g;
const hasSlot = (s: string) => { slotRe.lastIndex = 0; return slotRe.test(s); };
const isGloss = (s: string) => /[Ѐ-ӿ]/.test(s) || /^[QА]\s|«|»/.test(s); // Cyrillic / Q&A gloss lines

// Samples workshop (reusable for every course): pick a built pattern+day, choose the exact clip window,
// generate → cut the 60s (or chosen) clip AND render the companion page for that SAME pattern, then
// test-listen (audio players) and see the matching book page (thumbnails) before/after adding. Writes the
// public URLs into the LDP audio_samples / book_pages fields; each item is removable.
function SampleMaker(p: { lang: string; level: string; audio: string; pages: string; onField: (slot: string, v: string) => void }) {
  const [outs, setOuts] = useState<Array<{ file: string; pattern: number; day: number }>>([]);
  const [pick, setPick] = useState('');
  const [start, setStart] = useState(0);
  const [dur, setDur] = useState(60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    listOutputs(p.lang, p.level)
      .then((r) => { if (live) setOuts((r.outputs || []).map((o) => ({ file: o.file, pattern: o.pattern, day: o.day }))); })
      .catch(() => { if (live) setOuts([]); });
    return () => { live = false; };
  }, [p.lang, p.level]);

  const urlsOf = (blob: string) => blob.split('\n').map((s) => s.replace(/^[-*]\s*/, '').trim()).filter(Boolean);
  const audioUrls = urlsOf(p.audio);
  const pageUrls = urlsOf(p.pages);
  const setUrls = (slot: 'audio_samples' | 'book_pages', urls: string[]) => p.onField(slot, Array.from(new Set(urls)).map((u) => `- ${u}`).join('\n'));

  const gen = async () => {
    const o = outs.find((x) => x.file === pick); if (!o) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const a = await makeSample(p.lang, p.level, o.file, dur, start);
      setUrls('audio_samples', [...audioUrls, a.sample_url]);
      let pageMsg = '';
      try {
        const pg = await makeSamplePage('companion', bookLangOf(p.lang), p.level, o.pattern);
        const u = Object.values(pg.sample_pages || {})[0];
        if (u) { setUrls('book_pages', [...pageUrls, u]); pageMsg = ' + matching book page'; }
      } catch { /* no companion page for this level yet — audio still added */ }
      setMsg(`Added Pattern ${o.pattern} clip (${start}s–${start + dur}s)${pageMsg}. Test-listen below.`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Generation failed — check the pattern is built for this level.'); }
    finally { setBusy(false); }
  };

  const inp: CSSProperties = { background: 'var(--raised)', color: 'var(--text)', border: '1px solid var(--hair)', borderRadius: 8, padding: '6px 8px', fontSize: 13 };
  const num: CSSProperties = { ...inp, width: 64 };
  const rm: CSSProperties = { background: 'transparent', border: '1px solid var(--hair)', color: 'var(--muted)', borderRadius: 6, padding: '2px 8px', fontSize: 12, cursor: 'pointer' };
  return (
    <div style={{ margin: '10px 0', padding: 12, border: '1px solid var(--hair)', borderRadius: 10, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select value={pick} onChange={(e) => setPick(e.target.value)} style={inp}>
          <option value="">{outs.length ? `Pick a pattern (${outs.length} built)…` : 'No built patterns for this level yet'}</option>
          {outs.map((o) => <option key={o.file} value={o.file}>Pattern {o.pattern} · Day {o.day}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>start <input type="number" min={0} value={start} onChange={(e) => setStart(Math.max(0, Number(e.target.value) || 0))} style={num} />s</label>
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>length <input type="number" min={5} max={60} value={dur} onChange={(e) => setDur(Math.min(60, Math.max(5, Number(e.target.value) || 60)))} style={num} />s</label>
        <button onClick={gen} disabled={!pick || busy}
          style={{ background: 'var(--jade-dim)', color: '#eafff8', border: 0, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: pick && !busy ? 'pointer' : 'default', opacity: !pick || busy ? 0.5 : 1 }}>
          {busy ? 'Generating…' : 'Generate clip + matching page'}
        </button>
      </div>
      {msg && <div style={{ color: 'var(--savings)', fontSize: 12, marginTop: 6 }}>{msg}</div>}
      {err && <div style={{ color: '#E0736A', fontSize: 12, marginTop: 6 }}>{err}</div>}

      {audioUrls.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="slotlabel">Audio samples — test-listen</div>
          {audioUrls.map((u) => (
            <div key={u} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
              <audio controls preload="none" src={u} style={{ height: 34, flex: 1, maxWidth: 440 }} />
              <button onClick={() => setUrls('audio_samples', audioUrls.filter((x) => x !== u))} style={rm}>remove</button>
            </div>
          ))}
        </div>
      )}
      {pageUrls.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="slotlabel">Matching book pages — the text buyers see with the audio</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {pageUrls.map((u) => (
              <div key={u} style={{ position: 'relative' }}>
                <a href={u} target="_blank" rel="noreferrer"><img src={u} alt="sample page" style={{ width: 120, borderRadius: 8, border: '1px solid var(--hair)', display: 'block' }} /></a>
                <button onClick={() => setUrls('book_pages', pageUrls.filter((x) => x !== u))} title="remove"
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.6)', border: 0, color: '#fff', borderRadius: 6, padding: '1px 6px', fontSize: 12, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LdpDesignedEditor(p: {
  body: string; fields: Fields; prose: Prose; meta: LdpMeta; lang: string; level: string;
  onField: (slot: string, v: string) => void; onProse: (lineIdx: number, v: string) => void;
}) {
  const blocks = useMemo(() => parseBlocks(p.body), [p.body]);
  const injected = useMemo(() => new Set(p.meta.injected), [p.meta]);
  const A = useMemo(() => new Set(p.meta.classes.A), [p.meta]);
  const editableSlot = (n: string) => !injected.has(n) && (!A.has(n) || n === 'audio_samples' || n === 'book_pages');
  const fv = (n: string) => (p.fields[n] || '');

  // render a line that may contain slots: fixed text as styled spans, slots as inline/derived/chip.
  // Long block slots are pulled out as their own full-width editor by the caller (renderContentLine).
  const inlineParts = (text: string) => {
    const nodes: React.ReactNode[] = [];
    let last = 0; let m: RegExpExecArray | null; slotRe.lastIndex = 0; let k = 0;
    while ((m = slotRe.exec(text))) {
      if (m.index > last) nodes.push(<span key={`t${k}`} dangerouslySetInnerHTML={{ __html: mk(text.slice(last, m.index)) }} />);
      const n = m[1] ?? '';
      if (injected.has(n)) nodes.push(<span key={`c${k}`} className="chip" title="the store fills this at checkout">{n}</span>);
      else if (!editableSlot(n)) nodes.push(<span key={`d${k}`} className="py">{fv(n).trim() || n}</span>);
      else nodes.push(<InlineEd key={`e${k}`} value={fv(n)} placeholder={n} onChange={(v) => p.onField(n, v)} />);
      last = m.index + m[0].length; k++;
    }
    if (last < text.length) nodes.push(<span key={`t${k}`} dangerouslySetInnerHTML={{ __html: mk(text.slice(last)) }} />);
    return nodes;
  };

  // A single "big" slot on its own line → full-width block editor. Bullet lists collected → marks list.
  const BIG = new Set(['pain_scene', 'situations', 'credential_language', 'marketplace_extra', 'benefit_extra', 'proof_block', 'social_proof', 'ps_scene', 'bonuses', 'voices_line', 'audio_samples', 'book_pages', 'lie_1', 'lie_2', 'lie_3']);
  const soleSlot = (text: string): string | null => { const m = text.trim().match(/^\{\{(\w+)\}\}$/); return m ? (m[1] ?? null) : null; };

  // render the content lines of a block generically (paras, marks lists, gloss, big slots, prose lines)
  const renderLines = (lines: Line[], opts: { lead?: boolean } = {}) => {
    const out: React.ReactNode[] = [];
    let bullets: Line[] = [];
    const flushBullets = () => {
      if (!bullets.length) return;
      const items = bullets;
      bullets = [];
      out.push(
        <ul className="marks" key={`u${items[0]!.i}`}>
          {items.map((l) => {
            // a bullet carrying a {{slot}} stays slot-editable; a fixed-prose bullet becomes an editable
            // prose override (the "- " prefix is kept in the stored line so it stays a bullet on render).
            if (hasSlot(l.text)) return <li key={l.i}>{inlineParts(l.text.replace(/^[-*]\s+/, ''))}</li>;
            const cur = p.prose[l.i] ?? l.text;
            return <li key={l.i}><BlockEd value={cur.replace(/^[-*]\s+/, '')} onChange={(v) => p.onProse(l.i, `- ${v}`)} /></li>;
          })}
        </ul>,
      );
    };
    lines.forEach((l, idx) => {
      const t = l.text;
      if (!t.trim()) { flushBullets(); return; }
      const sole = soleSlot(t);
      if (sole && BIG.has(sole)) {
        flushBullets();
        out.push(<BlockEd key={l.i} value={fv(sole)} label={sole} placeholder={sole} empty={editableSlot(sole) && !fv(sole).trim()} onChange={(v) => p.onField(sole, v)} style={isGloss(fv(sole)) ? { fontFamily: 'var(--mono)' } : undefined} />);
        return;
      }
      if (/^[-*]\s+/.test(t)) { bullets.push(l); return; }
      flushBullets();
      if (isGloss(t) && !hasSlot(t)) { out.push(<pre className="gloss" key={l.i}><BlockEd value={p.prose[l.i] ?? t} onChange={(v) => p.onProse(l.i, v)} /></pre>); return; }
      if (hasSlot(t)) { out.push(<p className={opts.lead && idx === 0 ? 'lead' : undefined} key={l.i}>{inlineParts(t)}</p>); return; }
      // pure-prose line → editable prose override
      out.push(<p className={opts.lead && idx === 0 ? 'lead' : undefined} key={l.i}><BlockEd value={p.prose[l.i] ?? t} onChange={(v) => p.onProse(l.i, v)} /></p>);
    });
    flushBullets();
    return out;
  };

  const byNum = (n: number) => blocks.find((b) => b.n === n);
  const bySection = (name: string) => blocks.find((b) => b.section && b.section.startsWith(name));

  // dropdown accordions (from fields), matching the live layout
  const dropdowns = [1, 2, 3, 4].map((i) => ({ i, t: `dropdown_${i}_title`, b: `dropdown_${i}_body` }));

  return (
    <div className="ldpv">
      <style>{LANDING_CSS}</style>
      <div className="hazard" />
      <div className="ldp-page">

        {/* HERO — blocks 1,2,3 */}
        <header className="hero">
          {byNum(1) && <div className="eyebrow">{inlineParts((byNum(1)!.lines.find((l) => l.text.trim()) || { text: '' }).text)}</div>}
          {byNum(2) && <h1>{inlineParts((byNum(2)!.lines.find((l) => l.text.trim()) || { text: '' }).text)}</h1>}
          {byNum(3) && (() => {
            const ls = byNum(3)!.lines.filter((l) => l.text.trim());
            return ls.map((l, idx) => {
              if (soleSlot(l.text) === 'situations') return <ul className="phrases" key={l.i}>{fv('situations').split('\n').filter((x) => x.trim()).map((x, j) => <li key={j}>{x}</li>)}</ul>;
              if (idx === 0) return <p className="subhead" key={l.i}>{hasSlot(l.text) ? inlineParts(l.text) : <BlockEd value={p.prose[l.i] ?? l.text} onChange={(v) => p.onProse(l.i, v)} />}</p>;
              return hasSlot(l.text) ? <p key={l.i}>{inlineParts(l.text)}</p> : <p key={l.i}><BlockEd value={p.prose[l.i] ?? l.text} onChange={(v) => p.onProse(l.i, v)} /></p>;
            });
          })()}
          <div className="cta-wrap"><span className="cta">Get instant access</span></div>
        </header>

        {byNum(4) && <section>{renderLines(byNum(4)!.lines, { lead: true })}</section>}
        {byNum(5) && <section>{renderLines(byNum(5)!.lines, { lead: true })}</section>}

        {/* WHY explainers → accordion */}
        <section>
          <span className="kicker">Why this works</span>
          {dropdowns.map((d) => (
            <div className="acc" key={d.i}>
              <div className="acc-h">{editableSlot(d.t) ? <BlockEd value={fv(d.t)} placeholder={`${d.t} (leave blank to hide)`} onChange={(v) => p.onField(d.t, v)} /> : fv(d.t)}<span className="ic">+</span></div>
              <div className="acc-b"><BlockEd value={fv(d.b)} placeholder={d.b} onChange={(v) => p.onField(d.b, v)} style={isGloss(fv(d.b)) ? { fontFamily: 'var(--mono)' } : undefined} /></div>
            </div>
          ))}
        </section>

        {byNum(6) && <section>{renderLines(byNum(6)!.lines, { lead: true })}</section>}
        {byNum(7) && <section><span className="kicker">Who's teaching you</span>{renderLines(byNum(7)!.lines)}</section>}
        {byNum(8) && <section>{renderLines(byNum(8)!.lines, { lead: true })}</section>}

        {/* SAMPLES */}
        <section>
          <span className="kicker">Hear it, see it</span>
          <h2>Hear a minute of the training</h2>
          <SampleMaker lang={p.lang} level={p.level} audio={fv('audio_samples')} pages={fv('book_pages')} onField={p.onField} />
          <BlockEd label="audio_samples (one URL per line — the Generate button adds them; edit/remove here)" value={fv('audio_samples')} placeholder="- https://…mp3" onChange={(v) => p.onField('audio_samples', v)} />
          <BlockEd label="book_pages (one URL per line — companion sample pages)" value={fv('book_pages')} placeholder="- https://…png" onChange={(v) => p.onField('book_pages', v)} />
        </section>

        {byNum(9) && <section><span className="kicker">What travellers say</span><h2>Testimonials</h2>{renderLines(byNum(9)!.lines)}</section>}
        {byNum(10) && <section><span className="kicker">Zero risk</span>{renderLines(byNum(10)!.lines, { lead: true })}</section>}

        {/* BONUSES — cards from the bonuses list */}
        {byNum(11) && (
          <section>
            <span className="kicker">Included free</span><h2>Bonus books</h2>
            <BlockEd value={fv('bonuses')} label="bonuses (one per line)" placeholder="- Bonus book" onChange={(v) => p.onField('bonuses', v)} />
            <div style={{ marginTop: 12 }}>
              {fv('bonuses').split('\n').map((x) => x.replace(/^[-*]\s+/, '').trim()).filter(Boolean).map((name, j) => (
                <div className="bonus" key={j}><div className="tag">Bonus</div><div style={{ fontWeight: 700, marginTop: 4 }}>{name}</div></div>
              ))}
            </div>
          </section>
        )}

        {byNum(12) && <section><span className="kicker">What this is worth</span>{renderLines(byNum(12)!.lines)}</section>}

        {/* PRICE REVEAL */}
        {byNum(13) && (
          <section className="reveal">
            {byNum(13)!.lines.filter((l) => l.text.trim()).map((l) => <p key={l.i} className="anchors">{inlineParts(l.text)}</p>)}
            <div className="big"><span className="chip">your_price</span></div>
            <div className="cta-wrap"><span className="cta">Get instant access</span></div>
          </section>
        )}

        {/* SCARCITY */}
        {byNum(14) && <section><div className="scarcity">{renderLines(byNum(14)!.lines)}</div></section>}
        {byNum(15) && <section>{renderLines(byNum(15)!.lines, { lead: true })}</section>}
        {byNum(16) && <section style={{ textAlign: 'center' }}>{renderLines(byNum(16)!.lines, { lead: true })}<div className="cta-wrap"><span className="cta">Get instant access</span></div></section>}
        {byNum(17) && <section><p className="ps">{byNum(17)!.lines.filter((l) => l.text.trim()).map((l) => <span key={l.i}>{inlineParts(l.text)} </span>)}</p></section>}

        {/* FUND — the bottom-of-page line, editable as clean text (tailor per country) */}
        {bySection('Upsell and fund') && (() => {
          const fl = bySection('Upsell and fund')!.lines.filter((l) => l.text.trim());
          const footer = fl.find((l) => /bottom-of-page/i.test(l.text));
          const quoted = (t: string) => { const m = t.match(/"([\s\S]*)"/); return m ? (m[1] ?? t) : t; };
          const setQuoted = (l: Line, v: string) => { const base = p.prose[l.i] ?? l.text; p.onProse(l.i, base.replace(/"[\s\S]*"/, `"${v.replace(/"/g, '')}"`)); };
          return (
            <section>
              <div className="fund">
                {footer
                  ? <BlockEd label="Bottom-of-page fund line — tailor to this country" value={quoted(p.prose[footer.i] ?? footer.text)} onChange={(v) => setQuoted(footer, v)} />
                  : renderLines(fl)}
              </div>
            </section>
          );
        })()}

        {/* POST-PURCHASE (success page) — shown for completeness, editable prose */}
        {bySection('Post-purchase') && (
          <section>
            <span className="kicker">After purchase · success page</span>
            {renderLines(blocks.filter((b) => b.section && (b.section.startsWith('Post-purchase') || b.section.startsWith('How to use') || b.section.startsWith('Pro/Plus'))).flatMap((b) => b.lines))}
          </section>
        )}

      </div>
    </div>
  );
}
